import { Action, createReducer, on, createFeatureSelector, createSelector } from '@ngrx/store';
import { Promotion } from '@app/shared/models/promotion';
import { UserPromotionActions } from '../actions';

export const promotionFeatureKey = 'userPromotion';

export interface State {
  promotionsLoaded: Promotion[];
  promotionsLoading: boolean;
  promotionToCreate: Promotion;
  promotionCreating: boolean;
  promotionCreationError: any;
  promotionToUpdate: any;
  promotionToDelete: Promotion;
  promotionToDeleteDeleting: boolean;
  promotionUpdating: boolean;
  promotionUpdateError: any;
  processing: boolean;
  promotionUpdated: boolean;
  promotionInactivated: boolean;
  errorMessage: string;
}

export const initialState: State = {
  promotionsLoaded: [],
  promotionsLoading: false,
  promotionToCreate: {
    title: '',
    text: '',
    teaser: '',
    button: 'En savoir plus',
    link: '',
    role: 'Receiver',
    created: null,
    medias: [],
    language: null,
    status: ''
  },
  promotionCreating: false,
  promotionCreationError: null,
  promotionToUpdate: {
    title: '',
    text: '',
    status: '',
    language: '',
    teaser: '',
    button: '',
    link: '',
    role: '',
    created: null,
    medias: [],
    mediaToCreate: [],
    mediasToDelete: []
  },
  promotionUpdating: false,
  promotionUpdateError: null,
  promotionToDelete: null,
  promotionToDeleteDeleting: null,
  processing: null,
  promotionUpdated: false,
  promotionInactivated: false,
  errorMessage: '',
};

const promotionReducer = createReducer(
  initialState,
  on(UserPromotionActions.loadPromotion, (state) => ({
    ...state,
    promotionToUpdate: null,
    promotionUpdating: true,
  })),
  on(UserPromotionActions.loadPromotionSuccess, (state, { promotion }) => ({
    ...state,
    promotionToUpdate: {
      ...promotion,
      medias: [...promotion.medias],
      mediaToCreate: [],
      mediasToDelete: []
    },
    promotionUpdating: false,
    promotionsToUpdateLoading: false
  })),
  on(UserPromotionActions.loadPromotionFailure, (state, { errorMessage }) => ({
    ...state,
    errorMessage,
    promotionUpdating: false,
    promotionsToUpdateLoading: false
  })),
  on(UserPromotionActions.loadTeamPromotions, (state) => ({
    ...state,
    ...initialState,
    promotionsLoading: true,
  })),
  on(UserPromotionActions.loadPromotionsSuccess, (state, { promotions }) => ({
    ...state,
    promotionsLoaded: promotions,
    promotionsLoading: false
  })),
  on(UserPromotionActions.loadPromotionsFailure, (state, { errorMessage }) => ({
    ...state,
    errorMessage,
    promotionsLoading: false
  })),
  on(UserPromotionActions.createPromotion, (state) => ({
    ...state,
    promotionCreating: true,
  })),
  on(UserPromotionActions.createPromotionSuccess, (state) => ({
    ...state,
    promotionCreating: false,
    promotionToCreate: initialState.promotionToCreate
  })),
  on(UserPromotionActions.createPromotionFailure, (state, { error }) => ({
    ...state,
    promotionCreating: false,
    promotionCreationError: error
  })),
  on(UserPromotionActions.updatePromotionToCreate, (state, { data }) => ({
    ...state,
    promotionToCreate: { ...state.promotionToCreate, ...data, button: data.button.value },
  })),
  on(UserPromotionActions.addVideoToPromotionToCreateSuccess, (state, { video }) => ({
    ...state,
    promotionToCreate: {
      ...state.promotionToCreate,
      medias: [
        ...state.promotionToCreate.medias,
        {
          ...video,
          object_id: 'local_' + Date.now()
        }]
    }
  })),
  on(UserPromotionActions.addImageToPromotionToCreateSuccess, (state, { image }) => ({
    ...state,
    promotionToCreate: {
      ...state.promotionToCreate,
      medias: [
        ...state.promotionToCreate.medias,
        {
          ...image,
          sourceUrl: image.downloadUrl,
          thumbnail: image.downloadUrl,
          type: 'Image',
          object_id: 'local_' + Date.now()
        }
      ]
    }
  }
  )),
  on(UserPromotionActions.removeMediaFromPromotionToCreate, (state, { media }) => ({
    ...state,
    promotionToCreate: { ...state.promotionToCreate, medias: state.promotionToCreate.medias.filter(_media => _media.object_id !== media.object_id) }
  })),
  on(UserPromotionActions.cleanPromotionToCreate, (state) => ({
    ...state,
    promotionToCreate: initialState.promotionToCreate
  })),


  // UPDATE PROMOTION
  on(UserPromotionActions.selectPromotionToUpdate, (state, { promotion }) => ({
    ...state,
    promotionToUpdate: promotion,
  })),
  on(UserPromotionActions.cleanPromotionToUpdate, (state) => ({
    ...state,
    promotionToUpdate: initialState.promotionToUpdate
  })),
  on(UserPromotionActions.updatePromotion, (state) => ({
    ...state,
    promotionUpdating: true,
    processing: true,
    promotionUpdated: initialState.promotionUpdated,
  })),
  on(UserPromotionActions.updatePromotionSuccess, (state) => ({
    ...state,
    promotionUpdating: false,
    processing: false,
    promotionUpdated: true,
  })),
  on(UserPromotionActions.updatePromotionFailure, (state, { error }) => ({
    ...state,
    promotionUpdating: false,
    processing: false,
    promotionUpdateError: error,
    promotionUpdated: initialState.promotionUpdated,
  })),
  on(UserPromotionActions.updatePromotionToUpdateTemp, (state, { data }) => {
    return {
      ...state,
      promotionToUpdate: { ...state.promotionToUpdate, ...data, button: data.button.value },
    }
  }),
  on(UserPromotionActions.removeMediaFromPromotionToUpdate, (state, { media }) => {
    if (media.id) {
      return {
        ...state,
        promotionToUpdate: {
          ...state.promotionToUpdate,
          medias: state.promotionToUpdate.medias.filter(_media => _media.id !== media.id),
          mediasToDelete: [...state.promotionToUpdate.mediasToDelete, media]
        }
      };
    } else {
      return {
        ...state,
        promotionToUpdate: {
          ...state.promotionToUpdate,
          medias: state.promotionToUpdate.medias.filter(_media => _media.object_id !== media.object_id),
          mediasToCreate: state.promotionToUpdate.mediasToCreate.filter(_media => _media.object_id !== media.object_id)
        }
      };
    }
  }),
  on(UserPromotionActions.addVideoToPromotionToUpdateSuccess, (state, { video }) => ({
    ...state,
    promotionToUpdate: {
      ...state.promotionToUpdate,
      medias: [...state.promotionToUpdate.medias, { ...video, object_id: 'local_' + Date.now() }],
      mediaToCreate: [...state.promotionToUpdate.mediaToCreate, { ...video, object_id: 'local_' + Date.now() }]
    }
  })),
  on(UserPromotionActions.addImageToPromotionToUpdateSuccess, (state, { image }) => {
    return {
      ...state,
      promotionToUpdate: {
        ...state.promotionToUpdate,
        medias: [
          ...state.promotionToUpdate.medias,
          {
            ...image,
            sourceUrl: image.downloadUrl,
            thumbnail: image.downloadUrl,
            type: 'Image',
            object_id: 'local_' + Date.now()
          }
        ],
        mediaToCreate: [
          ...state.promotionToUpdate.mediaToCreate,
          {
            ...image,
            sourceUrl: image.downloadUrl,
            thumbnail: image.downloadUrl,
            type: 'Image',
            object_id: 'local_' + Date.now()
          }
        ]
      }
    }

  }),

  // DELETE PROMOTION
  on(UserPromotionActions.selectPromotionToDelete, (state, { promotion }) => ({
    ...state,
    promotionToDelete: promotion,
  })),
  on(UserPromotionActions.deletePromotion, (state) => ({
    ...state,
    processing: true,
    promotionInactivated: initialState.promotionInactivated,
  })),
  on(UserPromotionActions.deletePromotionSuccess, (state) => ({
    ...state,
    processing: false,
    promotionInactivated: true,
  })),
  on(UserPromotionActions.deletePromotionFailure, (state) => ({
    ...state,
    processing: false,
    promotionInactivated: initialState.promotionInactivated,
  })),

);

export function reducer(state: State | undefined, action: Action) {
  return promotionReducer(state, action);
}

export const getUserPromotionState = createFeatureSelector<State>('userPromotion');



export const getPromotionToCreate = createSelector(
  getUserPromotionState,
  (state: State) => state.promotionToCreate
);

export const getPromotionCreating = createSelector(
  getUserPromotionState,
  (state: State) => state.promotionCreating
);

export const getPromotionToUpdate = createSelector(
  getUserPromotionState,
  (state: State) => state.promotionToUpdate
);

export const getPromotionToDelete = createSelector(
  getUserPromotionState,
  (state: State) => state.promotionToDelete
);

export const getPromotionUpdating = createSelector(
  getUserPromotionState,
  (state: State) => state.promotionUpdating
);

export const getPromotionsLoaded = createSelector(
  getUserPromotionState,
  (state: State) => state.promotionsLoaded
);
export const getPromotionsLoading = createSelector(
  getUserPromotionState,
  (state: State) => state.promotionsLoading
);

export const getPromotionToDeleteDeleting = createSelector(
  getUserPromotionState,
  (state: State) => state.promotionToDeleteDeleting
);

export const getPromotionProcessing = createSelector(
  getUserPromotionState,
  (state: State) => state.processing
);

export const getPromotionUpdated = createSelector(
  getUserPromotionState,
  (state: State) => state.promotionUpdated
);

export const getPromotionInactivated = createSelector(
  getUserPromotionState,
  (state: State) => state.promotionInactivated
);

export const getPromotionCreationError = createSelector(
  getUserPromotionState,
  (state: State) => state.promotionCreationError
);